/**
 * Consolidates stream and connection counts for each appliance id for the Dashboard page.
 * @param {array} streamCountArray array containing objects for an appliance with the properties
 * 'id' 'name' and 'connection count' or 'stream count'
 * @returns {array} an array of objects containing the stream count and connection count for each appliance.
 */
export function formatStreamCounts(streamCountArray) {
  const countsByAppliance = {};
  streamCountArray.forEach((countObj) => {
    const {
      id, name, type, count,
    } = countObj;
    const formattedType = type.replace(' ', '_').trim();

    if (!countsByAppliance[id]) {
      countsByAppliance[id] = { name };
    }
    countsByAppliance[id][formattedType] = count;
  });

  return Object.values(countsByAppliance);
};

/**
 * Formats an array of items into a string with each item prefixed by a bullet point (•)
 * and separated by an HTML line break (<br/>).
 *
 * @param {Array<string>} items - The array of strings to format.
 * @returns {string} A formatted string with bullet points and line breaks, or an empty string if the input is not a
 * valid array or is empty.
 */
export const formatMessage = (items) => {
  if (!Array.isArray(items) || items.length === 0) {
    return '';
  }
  return items.map((item) => `• ${item}`).join('<br/>');
};

/**
 * Generates a success message based on the action performed, the type of item, and the number of successful items.
 *
 * @param {string} action - The action performed (e.g., "create", "update", "delete").
 * @param {string} itemType - The type of item being acted upon (e.g., "file", "record").
 * @param {Array} success - An array of successfully processed items.
 * @returns {string} A formatted success message indicating the action, item type, and count.
 */
export const generateSuccessMessage = (action, itemType, success) => {
  return `Successfully ${action}d ${success.length} ${success.length > 1 ? itemType + 's' : itemType}.`;
};

/**
 * Generates a failure message based on the provided action, item type, and failure details.
 *
 * @param {string} action - The action that was attempted (e.g., 'update', 'delete', etc.).
 * @param {string} itemType - The type of item involved in the action (e.g., 'file', 'record', etc.).
 * @param {Array<string>} fail - An array of failure details or items that caused the failure.
 * @returns {string} A formatted failure message indicating the action, item type, and failure details.
 */
export const generateFailMessage = (action, itemType, fail) => {
  const list = formatMessage(fail);
  return `Failed to ${action === 'update' ? 'edit' : action} ${fail.length} ` +
    `${fail.length > 1 ? itemType + 's' : itemType}: <br/>${list}`;
};

/**
 * Generates a message indicating a partial failure by combining a success message and a failure message.
 *
 * @param {string} successMessage - The message describing the successful part of the operation.
 * @param {string} failMessage - The message describing the failed part of the operation.
 * @returns {string} A formatted string indicating a partial failure, including the success and failure messages.
 */
export const generatePartialFailMessage = (successMessage, failMessage) => {
  return `Partial failure. <br/>${successMessage} <br /><br />${failMessage}`;
};